import { useRef, useState, type ReactNode } from 'react';
import { Button, Card, Center, CloseButton, Group, Loader, SimpleGrid, Stack, Tabs, Text, Title } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useProjectAnalyses, type AnalysisStaff } from '@/api/analyses';
import { useCommentCounts } from '@/api/comments';
import { errorMessage } from '@/api/errors';
import { useProjectObjects, type DataObject } from '@/api/objects';
import { useProject, useProjectAudit, useShares } from '@/api/projects';
import { useProjectReports, type Report } from '@/api/reports';
import { EmptyState } from '@/components/EmptyState';
import { ButtonLink } from '@/components/links';
import { StaffBreadcrumbs } from '@/components/StaffBreadcrumbs';
import { StatusBadge } from '@/components/StatusBadge';
import { useFormat } from '@/components/useFormat';
import { usePageTitle } from '@/components/usePageTitle';
import { AnalysesTable } from '@/features/analyses/AnalysesTable';
import { PlanAnalysisModal } from '@/features/analyses/PlanAnalysisModal';
import { CreateReportModal } from '@/features/reports/CreateReportModal';
import { ReportsTable } from '@/features/reports/ReportsTable';
import { DeleteObjectModal } from '@/features/objects/DeleteObjectModal';
import { RegisterObjectModal } from '@/features/objects/RegisterObjectModal';
import { EditProjectModal } from '@/features/projects/EditProjectModal';
import { ShareModal } from '@/features/projects/ShareModal';
import { SharesTable } from '@/features/projects/SharesTable';
import { AuditTimeline } from '@/features/staff/AuditTimeline';
import { ExportProjectButton } from '@/features/staff/ExportProjectButton';
import { InternalComments } from '@/features/staff/InternalComments';
import { InternalStateChip } from '@/features/staff/InternalStateChip';
import { LifecycleButtons } from '@/features/staff/LifecycleButtons';
import { ProjectCostCard } from '@/features/staff/ProjectCostCard';
import { ObjectsByOrigin } from '@/features/objects/ObjectsByOrigin';
import { RegisterReferencedModal } from '@/features/staff/RegisterReferencedModal';
import {
  applyFilter,
  filterTab,
  parseFilter,
  PROJECT_TABS,
  ProjectStatusStrip,
  type ProjectFilter,
  type ProjectTab,
} from '@/features/staff/ProjectStatusStrip';
import { StaffObjectsTable } from '@/features/staff/StaffObjectsTable';
import { brand } from '@/theme/theme';

interface ProjectSearch {
  tab?: ProjectTab;
  filter?: ProjectFilter;
}

export const Route = createFileRoute('/_staff/staff/projects/$projectId')({
  validateSearch: (s: Record<string, unknown>): ProjectSearch => ({
    tab: (PROJECT_TABS as readonly unknown[]).includes(s.tab) ? (s.tab as ProjectTab) : undefined,
    filter: parseFilter(s.filter),
  }),
  component: StaffProjectPage,
});

/** What waits on staff in the overview: analyses to deliver, launch or rescue; objects to process or replace. */
const NEEDS_ANALYSIS = new Set(['review', 'ready', 'failed']);
const NEEDS_OBJECT = new Set(['verified', 'rejected']);

function StaffProjectPage() {
  const { t } = useTranslation('projects');
  const { t: tr } = useTranslation('reports');
  const { projectId } = Route.useParams();
  const { tab = 'overview', filter } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const setView = (next: ProjectSearch) => void navigate({ search: next, replace: true });
  const project = useProject(projectId);
  const objects = useProjectObjects(projectId);
  const shares = useShares(projectId);
  const [auditOpen, setAuditOpen] = useState(false);
  const audit = useProjectAudit(projectId, auditOpen);
  const analyses = useProjectAnalyses(projectId);
  const reports = useProjectReports(projectId);
  const commentCounts = useCommentCounts(projectId);
  const { t: ta } = useTranslation('analyses');
  const [planOpen, setPlanOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const { date } = useFormat();
  const [shareOpen, setShareOpen] = useState(false);
  const [toDelete, setToDelete] = useState<DataObject | null>(null);
  const [toDeleteRaw, setToDeleteRaw] = useState<DataObject | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [referencedOpen, setReferencedOpen] = useState(false);
  // The status strip's storage chip opens the objects tab at the storage card.
  const costCard = useRef<HTMLDivElement | null>(null);
  const scrollToCost = useRef(false);
  const showCost = () => {
    if (tab === 'objects' && costCard.current) {
      costCard.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    scrollToCost.current = true;
    setView({ tab: 'objects' });
  };
  const costCardRef = (el: HTMLDivElement | null) => {
    costCard.current = el;
    if (el && scrollToCost.current) {
      scrollToCost.current = false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  usePageTitle(project.data?.name ?? t('staff.projects'));

  if (project.isPending) {
    return (
      <Center py="xl">
        <Loader color="forest" />
      </Center>
    );
  }
  if (project.isError) return <EmptyState title={errorMessage(project.error)} />;
  const p = project.data;
  const reportsSection = (note: ReactNode, rows: Report[]) => (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{tr('title')}</Title>
        <Button variant="default" onClick={() => setReportOpen(true)} disabled={p.state !== 'active'}>
          {tr('create.button')}
        </Button>
      </Group>
      {note}
      {reports.isPending ? (
        <Loader color="forest" />
      ) : (reports.data ?? []).length === 0 ? (
        <Text fz={14} c={brand.textMuted}>
          {tr('empty')}
        </Text>
      ) : (
        <ReportsTable projectId={projectId} reports={rows} staff commentCounts={commentCounts.data} />
      )}
    </Stack>
  );
  const attentionAnalyses = (analyses.data ?? []).filter((a) => NEEDS_ANALYSIS.has(a.state ?? ''));
  const attentionObjects = (objects.data ?? []).filter((o) => !o.deleted && NEEDS_OBJECT.has(o.state ?? ''));

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <StaffBreadcrumbs project={p} />
          <Group gap="sm">
            <Title order={1}>{p.name}</Title>
            <InternalStateChip state={p.state ?? 'active'} />
            <StatusBadge status={p.client_status} scope="project" counts={p.analyses_summary} />
          </Group>
          <Text fz={13} c={brand.textMuted}>
            {p.crs} · {t('project.acquisitionDate')}: {date(p.acquisition_date)}
            {p.acquisition_period_end ? ` – ${date(p.acquisition_period_end)}` : ''} · {t('project.created')} {date(p.created_at)}
          </Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="default"
            onClick={() => {
              setEditKey((k) => k + 1);
              setEditOpen(true);
            }}
          >
            {t('edit.button')}
          </Button>
          <ExportProjectButton projectId={projectId} />
          <LifecycleButtons projectId={projectId} state={p.state ?? 'active'} />
        </Group>
      </Group>

      <ProjectStatusStrip
        objects={objects.data ?? []}
        analyses={analyses.data ?? []}
        reports={reports.data ?? []}
        filter={filter}
        onPick={(f) => setView(f === filter ? { tab } : { tab: filterTab(f), filter: f })}
        projectId={projectId}
        onCost={showCost}
      />

      <Tabs value={tab} onChange={(v) => setView({ tab: (v as ProjectTab) ?? 'overview' })} keepMounted={false}>
        <Tabs.List mb="md">
          {PROJECT_TABS.map((key) => (
            <Tabs.Tab key={key} value={key}>
              {t(`staff.tabs.${key}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="overview">
          <Stack gap="lg">
            {/* The map outputs lead the project page, as they did before the tabs. */}
            {reportsSection(null, reports.data ?? [])}
            <Title order={2}>{t('staff.needsAttention')}</Title>
            {analyses.isPending || objects.isPending ? (
              <Loader color="forest" />
            ) : attentionAnalyses.length + attentionObjects.length === 0 ? (
              <Text fz={14} c={brand.textMuted}>
                {t('staff.nothingWaiting')}
              </Text>
            ) : (
              <>
                {attentionAnalyses.length > 0 && <AnalysesTable analyses={attentionAnalyses} commentCounts={commentCounts.data} actions details />}
                {attentionObjects.length > 0 && (
                  <StaffObjectsTable objects={attentionObjects} onDelete={setToDelete} onDeleteRaw={setToDeleteRaw} commentCounts={commentCounts.data} />
                )}
              </>
            )}
            <InternalComments subject={projectId} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="analyses">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={2}>{ta('title')}</Title>
              <Button variant="default" onClick={() => setPlanOpen(true)} disabled={p.state !== 'active'}>
                {ta('plan.button')}
              </Button>
            </Group>
            <FilterNote table="analyses" filter={filter} onClear={() => setView({ tab })} />
            {analyses.isPending ? (
              <Loader color="forest" />
            ) : (analyses.data ?? []).length === 0 ? (
              <Text fz={14} c={brand.textMuted}>
                {ta('empty')}
              </Text>
            ) : (
              <>
                <AnalysesSummaryLine analyses={analyses.data ?? []} />
                <AnalysesTable analyses={applyFilter(analyses.data ?? [], 'analyses', filter)} commentCounts={commentCounts.data} actions details />
              </>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="objects">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={2}>{t('staff.objects')}</Title>
              <Group gap="xs">
                <ButtonLink variant="default" to="/staff/projects/$projectId/boundary" params={{ projectId: p.public_id }} disabled={p.state !== 'active'}>
                  {t('staff.drawBoundary')}
                </ButtonLink>
                <Button variant="default" onClick={() => setRegisterOpen(true)} disabled={p.state !== 'active'}>
                  {t('staff.registerObject')}
                </Button>
                <Button variant="default" onClick={() => setReferencedOpen(true)} disabled={p.state !== 'active'}>
                  {t('referenced.button')}
                </Button>
              </Group>
            </Group>
            <FilterNote table="objects" filter={filter} onClear={() => setView({ tab })} />
            {objects.isPending ? (
              <Loader color="forest" />
            ) : objects.isError ? (
              <EmptyState title={errorMessage(objects.error)} />
            ) : objects.data.length === 0 ? (
              <EmptyState title={t('staff.noObjects')} />
            ) : (
              <ObjectsByOrigin objects={applyFilter(objects.data, 'objects', filter)}>
                {(group) => <StaffObjectsTable objects={group} onDelete={setToDelete} onDeleteRaw={setToDeleteRaw} commentCounts={commentCounts.data} />}
              </ObjectsByOrigin>
            )}
            <ProjectCostCard projectId={projectId} cardRef={costCardRef} />
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="outputs">
          {reportsSection(<FilterNote table="reports" filter={filter} onClear={() => setView({ tab })} />, applyFilter(reports.data ?? [], 'reports', filter))}
        </Tabs.Panel>

        <Tabs.Panel value="comments">
          <Stack gap="lg">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              <InternalComments subject={projectId} />
              <Card>
                <Group justify="space-between" mb="sm">
                  <Title order={3}>{t('project.sharing')}</Title>
                  <Button size="xs" variant="default" onClick={() => setShareOpen(true)}>
                    {t('share.add')}
                  </Button>
                </Group>
                {shares.isPending ? <Loader size="sm" color="forest" /> : <SharesTable projectId={projectId} shares={shares.data ?? []} />}
              </Card>
            </SimpleGrid>

            <Group justify="space-between" align="center">
              <Title order={2}>{t('staff.audit')}</Title>
              {!auditOpen && (
                <Button variant="default" size="xs" onClick={() => setAuditOpen(true)}>
                  {t('staff.showAudit')}
                </Button>
              )}
            </Group>
            {auditOpen && (audit.isPending ? <Loader color="forest" /> : <AuditTimeline events={audit.data ?? []} />)}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      <EditProjectModal key={editKey} project={p} opened={editOpen} onClose={() => setEditOpen(false)} />
      <ShareModal projectId={projectId} opened={shareOpen} onClose={() => setShareOpen(false)} />
      <CreateReportModal projectId={projectId} opened={reportOpen} onClose={() => setReportOpen(false)} />
      <PlanAnalysisModal projectId={projectId} opened={planOpen} onClose={() => setPlanOpen(false)} delivered={(analyses.data ?? []).filter((a) => a.state === 'delivered')} />
      <RegisterObjectModal projectId={projectId} opened={registerOpen} onClose={() => setRegisterOpen(false)} existing={objects.data ?? []} goToUpload={false} allKinds />
      <RegisterReferencedModal projectId={projectId} opened={referencedOpen} onClose={() => setReferencedOpen(false)} existing={objects.data ?? []} analyses={analyses.data ?? []} />
      <DeleteObjectModal object={toDelete} onClose={() => setToDelete(null)} />
      <DeleteObjectModal object={toDeleteRaw} onClose={() => setToDeleteRaw(null)} mode="raw" />
    </Stack>
  );
}

/** How many analyses wait for review, were delivered or cancelled — the decisions the table's actions make. */
function AnalysesSummaryLine({ analyses }: { analyses: AnalysisStaff[] }) {
  const { t } = useTranslation('analyses');
  const count = (state: string) => analyses.filter((a) => a.state === state).length;
  return (
    <Text fz={13} c={brand.textMuted} mt={-8}>
      {t('summary.line', { review: count('review'), delivered: count('delivered'), cancelled: count('cancelled') })}
    </Text>
  );
}

/** Says which strip filter narrows this table, with a way to drop it. */
function FilterNote({ table, filter, onClear }: { table: 'objects' | 'analyses' | 'reports'; filter: ProjectFilter | undefined; onClear: () => void }) {
  const { t } = useTranslation('projects');
  if (!filter || !filter.startsWith(`${table}:`)) return null;
  return (
    <Group gap={4} mt={-8}>
      <Text fz={13} c={brand.textMuted}>
        {t('staff.strip.filtered', { filter: t(`staff.strip.${filter.replace(':', '.')}`) })}
      </Text>
      <CloseButton size="sm" aria-label={t('staff.strip.clear')} onClick={onClear} />
    </Group>
  );
}
